import Head from 'next/head';
import { NextSeo } from 'next-seo';
import DoubtSolution from '@cont/Doubts20/DoubtSolution/DoubtSolution';
import Layout from '@layout/index';
import { useEffect } from 'react';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import { fetchDoubtDetailsWithoutAuth } from '@api/fetchers/goal/custom-doubts';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { SWRConfig } from 'swr';
import useFullCustomDoubtsInfo from '@api/hooks/goal/custom-doubts/ueFullCustomDoubtsInfo';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import useURLs from '@api/hooks/useURLs';
import { DNS } from '@constants/segment';

const DoubtSolutionPage = ({
  goalUID,
  goalInfo,
  goalSlug,
  doubtUID,
  fallbackData
}) => {
  const { title } = goalInfo;
  const subjectTitle =
    fallbackData?.doubtDetailsWithoutAuthData?.subject?.title;
  const metaTitle = `${title} ${subjectTitle} Doubt Solution`;
  const metaDescription = `Get accurate ${title} ${subjectTitle} doubt solutions with clear explanations to strengthen your concepts and improve your exam performance.`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/doubts-and-solutions/doubt/${doubtUID}`;

  const {
    keys,
    revalidateSubjectAndLanguage,
    revalidateDoubtConfig,
    revalidateDoubtDetails,
    revalidateFeedBackChoices
  } = useFullCustomDoubtsInfo(goalUID, doubtUID, fallbackData);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  useEffect(() => {
    if (isLoggedIn) {
      revalidateDoubtConfig();
      revalidateSubjectAndLanguage();
      revalidateDoubtDetails();
      revalidateFeedBackChoices();
    }
  }, [
    isLoggedIn,
    revalidateSubjectAndLanguage,
    revalidateDoubtConfig,
    revalidateDoubtDetails,
    revalidateFeedBackChoices
  ]);

  useURLs();

  return (
    <>
      <SWRConfig
        value={{
          revalidateOnMount: false,
          dedupingInterval: 60 * 1000,
          fallback: {
            [keys.doubtDetailsWithoutAuthSWRKey]:
              fallbackData.doubtDetailsWithoutAuthData
          }
        }}
      >
        <NextSeo
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalURL}
          openGraph={{
            title: metaTitle,
            description: metaDescription,
            url: canonicalURL
          }}
        />
        <Head>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalURL} />
        </Head>
        <DoubtSolution goalUID={goalUID} goalInfo={goalInfo} />
      </SWRConfig>
    </>
  );
};

DoubtSolutionPage.pageInfo = {
  isMWebSupported: true,
  name: DNS,
  layoutOptions: {
    hideHeader: false,
    hideFooter: false,
    hideFreshbot: true
  }
};

export const getStaticProps = async ({ params }) => {
  try {
    const { goalUID, goalSlug, doubtUID } = params || {};
    // Handle invalid sitemap requests
    if (!goalUID || !goalSlug || !doubtUID) return { notFound: true };
    const goalInfo = await fetchGoalInfo(goalUID);
    const promises = [];
    promises.push(
      resolvePromiseToObject(
        fetchDoubtDetailsWithoutAuth(goalUID, doubtUID),
        'doubtDetailsWithoutAuthData'
      )
    );
    const fallbackData = await Promise.all(promises).then((resolvedValues) =>
      resolvedValues.reduce(
        (acc, next) => ({ ...acc, [next.type]: next.result }),
        {}
      )
    );

    return {
      props: {
        fallbackData: {
          ...fallbackData,
          goalData: goalInfo
        },
        goalInfo,
        goalUID,
        doubtUID,
        goalSlug,
        globalFallbackData: {
          [getTopologyKey(goalInfo.uid)]: goalInfo
        }
      }
    };
  } catch (error) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default DoubtSolutionPage;
DoubtSolutionPage.Layout = Layout;
DoubtSolutionPage.Wrapper = PageReduxProvider;
